import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import logger from '../../utils/logger';
import moment from 'moment';
import BreadCrumb from '../BreadCrumb';
import {
  ClipboardDocumentCheckIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowDownTrayIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';

const pages = [
  { name: 'Procurement', to: '/procurement', component: Link, current: false },
  { name: 'Goods Received Notes', to: '/procurement/grn', component: Link, current: true },
];

const GRNList = () => {
  const [grns, setGrns] = useState([]);
  const [filteredGrns, setFilteredGrns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchGRNs();
  }, []);

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grns, searchTerm, statusFilter, startDate, endDate]);

  const fetchGRNs = async () => {
    try {
      setLoading(true);
      const q = query(
        collection(db, 'goodsReceivedNotes'),
        orderBy('receivedDate', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const grnList = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setGrns(grnList);
    } catch (error) {
      logger.error('Error fetching GRNs:', error);
      toast.error('Failed to load goods received notes');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...grns];

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (grn) =>
          grn.grnNumber?.toLowerCase().includes(searchLower) ||
          grn.supplier?.toLowerCase().includes(searchLower) ||
          grn.poNumber?.toLowerCase().includes(searchLower)
      );
    }

    // Status filter
    if (statusFilter) {
      filtered = filtered.filter((grn) => grn.status === statusFilter);
    }

    // Date range filter
    if (startDate) {
      filtered = filtered.filter((grn) => {
        const grnDate = grn.receivedDate?.toDate ? grn.receivedDate.toDate() : new Date(grn.receivedDate);
        return moment(grnDate).isSameOrAfter(startDate, 'day');
      });
    }
    if (endDate) {
      filtered = filtered.filter((grn) => {
        const grnDate = grn.receivedDate?.toDate ? grn.receivedDate.toDate() : new Date(grn.receivedDate);
        return moment(grnDate).isSameOrBefore(endDate, 'day');
      });
    }

    setFilteredGrns(filtered);
    setCurrentPage(1);
  };

  const exportToCSV = () => {
    const headers = ['GRN Number', 'PO Number', 'Supplier', 'Received Date', 'Items', 'Status'];
    const rows = filteredGrns.map(grn => [
      grn.grnNumber,
      grn.poNumber || 'N/A',
      grn.supplier || 'N/A',
      grn.receivedDate?.toDate ? moment(grn.receivedDate.toDate()).format('YYYY-MM-DD') : 'N/A',
      grn.items?.length || 0,
      grn.status
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `grn-list-${moment().format('YYYY-MM-DD')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('CSV exported successfully');
  };

  // Pagination
  const indexOfLastGrn = currentPage * itemsPerPage;
  const indexOfFirstGrn = indexOfLastGrn - itemsPerPage;
  const currentGrns = filteredGrns.slice(indexOfFirstGrn, indexOfLastGrn);
  const totalPages = Math.ceil(filteredGrns.length / itemsPerPage);

  const getStatusBadge = (status) => {
    const badges = {
      'Fully Received': 'bg-emerald-100 text-emerald-800',
      'Partially Received': 'bg-amber-100 text-amber-800',
      'Discrepancy': 'bg-red-100 text-red-800',
      'Quality Issue': 'bg-orange-100 text-orange-800',
      'Accepted': 'bg-blue-100 text-blue-800',
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-500 to-emerald-600 py-4">
        <BreadCrumb pages={pages} />
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-500 to-emerald-600 py-4">
      <BreadCrumb pages={pages} />
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-4 mb-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl">
                <ClipboardDocumentCheckIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-black text-gray-900">Goods Received Notes</h1>
                <p className="text-xs text-gray-600 mt-0.5">
                  Track all received items with quality inspection
                </p>
              </div>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={exportToCSV}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-xl shadow-sm hover:bg-gray-50 transition-all duration-200"
              >
                <ArrowDownTrayIcon className="h-4 w-4 mr-1.5" />
                Export CSV
              </button>
              <Link
                to="/procurement/purchase-orders"
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl shadow-sm hover:shadow-md transition-all duration-200"
              >
                View Purchase Orders
              </Link>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
          <div className="bg-white rounded-xl shadow-lg p-4">
            <p className="text-xs font-medium text-gray-600">Total GRNs</p>
            <p className="text-2xl font-black text-gray-900 mt-1">{grns.length}</p>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-4">
            <p className="text-xs font-medium text-gray-600">Fully Received</p>
            <p className="text-2xl font-black text-emerald-600 mt-1">
              {grns.filter((grn) => grn.status === 'Fully Received').length}
            </p>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-4">
            <p className="text-xs font-medium text-gray-600">With Discrepancy</p>
            <p className="text-2xl font-black text-red-600 mt-1">
              {grns.filter((grn) => grn.status === 'Discrepancy' || grn.status === 'Quality Issue').length}
            </p>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-4">
            <p className="text-xs font-medium text-gray-600">This Month</p>
            <p className="text-2xl font-black text-blue-600 mt-1">
              {grns.filter((grn) => {
                const grnDate = grn.receivedDate?.toDate ? grn.receivedDate.toDate() : new Date(grn.receivedDate);
                return moment(grnDate).isSame(new Date(), 'month');
              }).length}
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-lg p-4 mb-4">
          <div className="flex items-center space-x-2 mb-3">
            <FunnelIcon className="h-4 w-4 text-gray-500" />
            <h3 className="text-sm font-bold text-gray-900">Filters</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            {/* Search */}
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by GRN, supplier, or PO..."
                className="block w-full pl-9 pr-3 py-1.5 text-xs rounded-xl border-gray-300 shadow-sm focus:border-green-500 focus:ring-2 focus:ring-green-500"
              />
            </div>

            {/* Status Filter */}
            <div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="block w-full px-3 py-1.5 text-xs rounded-xl border-gray-300 shadow-sm focus:border-green-500 focus:ring-2 focus:ring-green-500"
              >
                <option value="">All Statuses</option>
                <option value="Fully Received">Fully Received</option>
                <option value="Partially Received">Partially Received</option>
                <option value="Discrepancy">Discrepancy</option>
                <option value="Quality Issue">Quality Issue</option>
                <option value="Accepted">Accepted</option>
              </select>
            </div>

            {/* Start Date */}
            <div className="relative">
              <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                placeholder="Start Date"
                className="block w-full pl-9 pr-3 py-1.5 text-xs rounded-xl border-gray-300 shadow-sm focus:border-green-500 focus:ring-2 focus:ring-green-500"
              />
            </div>

            {/* End Date */}
            <div className="relative">
              <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                placeholder="End Date"
                className="block w-full pl-9 pr-3 py-1.5 text-xs rounded-xl border-gray-300 shadow-sm focus:border-green-500 focus:ring-2 focus:ring-green-500"
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-green-500 to-emerald-600">
                <tr>
                  <th className="px-2 py-1.5 text-left text-xs font-black text-white uppercase tracking-wider">
                    GRN Number
                  </th>
                  <th className="px-2 py-1.5 text-left text-xs font-black text-white uppercase tracking-wider">
                    Supplier
                  </th>
                  <th className="px-2 py-1.5 text-left text-xs font-black text-white uppercase tracking-wider">
                    Received Date
                  </th>
                  <th className="px-2 py-1.5 text-center text-xs font-black text-white uppercase tracking-wider">
                    Items
                  </th>
                  <th className="px-2 py-1.5 text-center text-xs font-black text-white uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-2 py-1.5 text-right text-xs font-black text-white uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentGrns.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-2 py-8 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <ClipboardDocumentCheckIcon className="h-12 w-12 text-gray-300 mb-3" />
                        <p className="text-sm font-medium text-gray-500">
                          {searchTerm || statusFilter || startDate || endDate
                            ? 'No GRNs found matching your filters'
                            : 'No goods received notes yet'}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  currentGrns.map((grn) => (
                    <tr key={grn.id} className="hover:bg-green-50 transition-colors duration-150">
                      <td className="px-2 py-1.5 whitespace-nowrap">
                        <div className="text-xs font-bold text-gray-900">
                          {grn.grnNumber}
                        </div>
                        {grn.poNumber && (
                          <div className="text-xs text-gray-500">
                            PO: {grn.poNumber}
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap text-xs font-medium text-gray-900">
                        {grn.supplier || 'N/A'}
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap text-xs font-medium text-gray-600">
                        {grn.receivedDate?.toDate
                          ? moment(grn.receivedDate.toDate()).format('MMM DD, YYYY')
                          : moment(grn.receivedDate).format('MMM DD, YYYY')}
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap text-center text-xs font-medium text-gray-900">
                        {grn.items?.length || 0} items
                        {grn.hasDiscrepancy && (
                          <span className="ml-2 text-red-600 font-bold">⚠</span>
                        )}
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap text-center">
                        <span
                          className={`px-2 py-1 inline-flex text-xs leading-tight font-bold rounded-full ${getStatusBadge(
                            grn.status
                          )}`}
                        >
                          {grn.status}
                        </span>
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap text-right">
                        <Link
                          to={`/procurement/grn/${grn.id}`}
                          className="text-xs font-bold text-green-600 hover:text-green-900 transition-colors duration-150"
                        >
                          View
                        </Link>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="text-xs text-gray-700">
                  Showing <span className="font-bold">{indexOfFirstGrn + 1}</span> to{' '}
                  <span className="font-bold">
                    {Math.min(indexOfLastGrn, filteredGrns.length)}
                  </span>{' '}
                  of <span className="font-bold">{filteredGrns.length}</span> results
                </div>
                <div className="flex space-x-1">
                  <button
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-3 py-1 text-xs font-medium rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150"
                  >
                    Previous
                  </button>
                  {[...Array(Math.min(totalPages, 5))].map((_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={i}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-1 text-xs font-medium rounded-lg transition-all duration-150 ${
                          currentPage === pageNum
                            ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-md'
                            : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 text-xs font-medium rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Results Summary */}
        <div className="text-xs text-white text-center mt-3 font-medium">
          Showing {currentGrns.length} of {filteredGrns.length} goods received notes
        </div>
      </div>
    </div>
  );
};

export default GRNList;
